import { useCallback, useEffect, useRef, useState } from "react";

import { isAPIError } from "@/core/services";

export type AsyncQueryStatus = "loading" | "success" | "error" | "forbidden";

export interface UseAsyncQueryOptions<T> {
  /** Busca o dado; use o `signal` na requisição (é abortado ao mudar a chave, recarregar ou desmontar). */
  fetcher: (signal: AbortSignal) => Promise<T>;
  /** Identifica a consulta: mudou, busca de novo. O `fetcher` em si não conta (pode ser novo a cada render). */
  queryKey: string;
  /** Dado exposto enquanto não há resposta e depois de um erro; mantenha a referência estável. */
  initialData: T;
  /** `false` não busca. Padrão: `true`. */
  enabled?: boolean;
  /** Ao buscar de novo, mantém o dado anterior em vez de voltar a `initialData`. */
  keepPrevious?: boolean;
  forbiddenMessage: string;
  errorMessage: string;
  /** Mensagem própria do erro (ex.: a do servidor); vazia ou ausente, vale `errorMessage`. Não vale para o 403. */
  resolveErrorMessage?: (error: unknown) => string | null | undefined;
}

export interface UseAsyncQueryResult<T> {
  status: AsyncQueryStatus;
  data: T;
  /** Mensagem do erro ou da falta de permissão; `null` fora desses estados. */
  errorMessage: string | null;
  /** O erro como veio, para quem precisa de mais do que a mensagem; `null` fora de `error` e `forbidden`. */
  error: unknown;
  /** Busca de novo com a mesma chave. */
  reload: () => void;
}

interface QueryState<T> {
  status: AsyncQueryStatus;
  data: T;
  errorMessage: string | null;
  error: unknown;
}

/*
 * Cache por `queryKey`, só dentro de um escopo aberto: fora dele a consulta busca a cada montagem, como sempre. Guarda só
 * respostas de sucesso; o escopo é por prefixo de chave e quem o abre limpa ao sair (sem dado preso entre aberturas).
 */
const escoposDeCache = new Map<string, number>();
const consultasEmCache = new Map<string, unknown>();
const consultasMontadas = new Set<{ queryKey: string; recarregar: () => void }>();
// Geração: cada limpeza avança o contador e marca o prefixo; a busca que começou antes dela não grava no cache.
let geracao = 0;
const limpezas = new Map<string, number>();

const limpaDepoisDe = (queryKey: string, iniciadaEm: number) =>
  [...limpezas].some(([prefixo, limpaEm]) => limpaEm > iniciadaEm && queryKey.startsWith(prefixo));

const comPrefixo = (prefixo: string, chaves: Iterable<string>) =>
  [...chaves].filter((chave) => chave.startsWith(prefixo));

const emEscopoDeCache = (queryKey: string) =>
  [...escoposDeCache.keys()].some((prefixo) => queryKey.startsWith(prefixo));

/**
 * Guarda as respostas das consultas cuja chave começa com `prefixo` e as reaproveita sem buscar de novo, até soltar.
 * Devolve a função que solta o escopo; o último a soltar limpa as entradas do prefixo.
 */
export function manterConsultasEmCache(prefixo: string): () => void {
  escoposDeCache.set(prefixo, (escoposDeCache.get(prefixo) ?? 0) + 1);
  let solto = false;

  return () => {
    if (solto) return;
    solto = true;
    const restantes = (escoposDeCache.get(prefixo) ?? 1) - 1;
    if (restantes > 0) {
      escoposDeCache.set(prefixo, restantes);
      return;
    }
    escoposDeCache.delete(prefixo);
    limparConsultas(prefixo);
  };
}

/** Descarta o cache das chaves que começam com `prefixo`; a próxima montagem dessas consultas busca de novo. */
export function limparConsultas(prefixo: string): void {
  geracao += 1;
  limpezas.set(prefixo, geracao);
  comPrefixo(prefixo, consultasEmCache.keys()).forEach((chave) => consultasEmCache.delete(chave));
}

/** Descarta o cache das chaves que começam com `prefixo` e recarrega na hora as consultas montadas com elas. */
export function invalidarConsultas(prefixo: string): void {
  limparConsultas(prefixo);
  consultasMontadas.forEach((consulta) => {
    if (consulta.queryKey.startsWith(prefixo)) consulta.recarregar();
  });
}

function lerDoCache<T>(queryKey: string): QueryState<T> | null {
  if (!consultasEmCache.has(queryKey)) return null;
  return {
    status: "success",
    data: consultasEmCache.get(queryKey) as T,
    errorMessage: null,
    error: null,
  };
}

/**
 * Consulta única com cancelamento, recarga e 403 como "sem permissão" (opções de filtro, fichas,
 * dados de formulário). Para listagem paginada com busca, use `useAsyncList`. Dentro de um escopo de
 * `manterConsultasEmCache`, a chave já respondida volta do cache sem buscar.
 */
export function useAsyncQuery<T>({
  fetcher,
  queryKey,
  initialData,
  enabled = true,
  keepPrevious = false,
  forbiddenMessage,
  errorMessage,
  resolveErrorMessage,
}: UseAsyncQueryOptions<T>): UseAsyncQueryResult<T> {
  const [state, setState] = useState<QueryState<T>>(
    () =>
      (enabled ? lerDoCache<T>(queryKey) : null) ?? {
        status: "loading",
        data: initialData,
        errorMessage: null,
        error: null,
      },
  );
  const [reloadToken, setReloadToken] = useState(0);

  const latest = useRef({
    fetcher,
    initialData,
    keepPrevious,
    forbiddenMessage,
    errorMessage,
    resolveErrorMessage,
  });
  latest.current = {
    fetcher,
    initialData,
    keepPrevious,
    forbiddenMessage,
    errorMessage,
    resolveErrorMessage,
  };

  useEffect(() => {
    if (!enabled) return undefined;

    const emCache = lerDoCache<T>(queryKey);
    if (emCache) {
      setState((previous) =>
        previous.status === "success" && previous.data === emCache.data ? previous : emCache,
      );
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const iniciadaEm = geracao;

    setState((previous) => ({
      status: "loading",
      data: latest.current.keepPrevious ? previous.data : latest.current.initialData,
      errorMessage: null,
      error: null,
    }));

    latest.current
      .fetcher(controller.signal)
      .then((data) => {
        if (cancelled) return;
        if (emEscopoDeCache(queryKey) && !limpaDepoisDe(queryKey, iniciadaEm)) {
          consultasEmCache.set(queryKey, data);
        }
        setState({ status: "success", data, errorMessage: null, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const current = latest.current;
        const isDenied = isAPIError(error) && error.status === 403;
        setState({
          status: isDenied ? "forbidden" : "error",
          data: current.initialData,
          errorMessage: isDenied
            ? current.forbiddenMessage
            : current.resolveErrorMessage?.(error) || current.errorMessage,
          error,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [queryKey, enabled, reloadToken]);

  // Recarregar é pedir dado novo: a entrada desta chave sai do cache antes.
  const reload = useCallback(() => {
    consultasEmCache.delete(queryKey);
    setReloadToken((token) => token + 1);
  }, [queryKey]);

  useEffect(() => {
    if (!enabled) return undefined;
    const consulta = { queryKey, recarregar: () => setReloadToken((token) => token + 1) };
    consultasMontadas.add(consulta);
    return () => {
      consultasMontadas.delete(consulta);
    };
  }, [queryKey, enabled]);

  return { ...state, reload };
}
